import { Button } from "@fluentui/react-components";
import loc from "owa-localize";
import { observer } from "owa-mobx-react";
import { useCallback, type FunctionComponent } from "react";
/* eslint-disable-next-line owa-custom-rules/forbid-svg-import -- Direct SVG import required; component-based icon (@fluentui/react-icons) not available for this custom Rewards medallion illustration */
import REWARDS_MEDALLION_SVG from "./RewardsMedallion.svg";
import { blockedFallbackDescription, blockedFallbackTitle, errorFallbackDescription, errorFallbackTitle, goToRewardsButton, mismatchFallbackDescription, mismatchFallbackTitle, rewardsSignInButton, } from "./RewardsFallback.locstring.json";
import { buttons, description, fallbackContainer, illustration, title, } from "./RewardsFallback.scss";

// Form/OCID tracking codes appended to the destination URLs so downstream analytics can attribute
// traffic that originates from this cookie-blocked fallback screen.
const GO_TO_REWARDS_URL = "https://rewards.bing.com/?form=MY02S8&OCID=MY02S8";

// Top-level Bing sign-in that, after the user authenticates, re-establishes the shared Bing session
// and lands them on the Rewards page. Opened in a new tab (never framed) so the interactive login
// UI—which Bing refuses to render inside an iframe—can appear. This is the recovery path when the
// flyout failed because the user is signed out of Bing, which the parent cannot distinguish from
// third-party cookies being blocked (both surface here with no ready signal from the flyout).
const SIGN_IN_URL = "https://www.bing.com/fd/auth/signin?action=interactive&provider=windows_live_id&return_url=" +
    encodeURIComponent(GO_TO_REWARDS_URL);

// The fallback screen covers three distinct failure cases, each with its own message and buttons:
//   'blocked'  - the flyout never loaded (no ready signal). The user is either signed out of Bing
//                or third-party cookies are blocked; these are indistinguishable client-side. The
//                Sign-in button is shown here because it is the recovery path for the signed-out
//                case (it re-establishes the Bing session in a new tab).
//   'mismatch' - the flyout loaded under a different, authenticated Bing account than the OWA user.
//                Sign-in is NOT shown: with an existing session it silently re-authenticates the
//                same wrong account, so it cannot help; the user must open Rewards or switch account.
//   'error'    - the flyout loaded but the Rewards backend failed. Nothing the user can do here, so
//                only the 'Go to Rewards' escape hatch is shown.
export interface RewardsFallbackProps {
    variant?: "blocked" | "mismatch" | "error";
}

const defaultExport: FunctionComponent<RewardsFallbackProps> = observer(function RewardsFallback({ variant = "blocked", }: RewardsFallbackProps) {
    const onSignIn = useCallback(() => {
        window.open(SIGN_IN_URL, "_blank", "noopener,noreferrer");
    }, []);
    const onGoToRewards = useCallback(() => {
        window.open(GO_TO_REWARDS_URL, "_blank", "noopener,noreferrer");
    }, []);
    
    // Sign-in only helps when there is no signed-in Bing session (the signed-out case, which
    // surfaces as 'blocked'). It is a no-op for a different signed-in account ('mismatch') and for
    // backend errors ('error'), so it is hidden there.
    const showSignIn = variant === "blocked";
    
    let titleText;
    let descriptionText;
    switch (variant) {
        case "mismatch":
            titleText = mismatchFallbackTitle;
            descriptionText = mismatchFallbackDescription;
            break;
        case "error":
            titleText = errorFallbackTitle;
            descriptionText = errorFallbackDescription;
            break;
        default:
            titleText = blockedFallbackTitle;
            descriptionText = blockedFallbackDescription;
            break;
    }
    
    return (<div className={fallbackContainer}>
            <img src={REWARDS_MEDALLION_SVG} alt="" aria-hidden="true" className={illustration}/>
            <h2 className={title}>{loc(titleText)}</h2>
            <p className={description}>{loc(descriptionText)}</p>
            <div className={buttons}>
                {showSignIn && (<Button appearance="primary" onClick={onSignIn}>
                        {loc(rewardsSignInButton)}
                    </Button>)}
                <Button appearance={showSignIn ? undefined : "primary"} onClick={onGoToRewards}>
                    {loc(goToRewardsButton)}
                </Button>
            </div>
        </div>);
}, "RewardsFallback");

export default defaultExport;
